import { ColumnSource, RequestFilters, RowSource } from "@omegagrid/grid";
import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { Dictionary } from "@reporting/rp-types/definition";

const PAGE_SIZE = 500;

export class DictionaryGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	afterLoad() {
		if (this.editor.hasAttribute('term')) this.setTerm(this.editor.getAttribute('term'));
	}

	async setTerm(term: string) {
		setTimeout(() => {
			this.editor.newRow(0);
			this.editor.setCellValue(0, 2, term);
		}, 100);
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 2,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red
			}, {
				action: 'duplicate',
				title: msg('Duplicate'),
				icon: 'copy',
				color: ColorTypes.Yellow
			}],
			filter: false,
			sorting: false,
		}, {
			data: 'lang',
			header: msg('Lang'),
			t: 't',
			e: true,
			w: 60
		}, {
			data: 'term',
			header: msg('Term'),
			t: 't',
			e: true,
			w: 300
		}, {
			data: 'translation',
			header: msg('Translation'),
			t: 't',
			e: true,
			w: 300
		}, {
			data: 'idz',
			t: 'n',
			hidden: true,
			e: false,
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const dictionary = await rp.ms('definition').get<Dictionary[]>(`dictionary`, this.createRequestParams(filters, {
			fields: ['idz', 'lang', 'term', 'translation'],
			order: [['lang', 'asc']],
			count: PAGE_SIZE
		}));
		return dictionary.map(data => ({ data }));
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: PAGE_SIZE,
			refreshAfterSave: true,
			saveMode: 'all_rows',
			showEmptyRow: true,
			rowMenu: {
				addRows: true,
				deleteRows: true,
			},
		});
	}

	async save(rowData: RowData[]) {
		const duplicates = [];
		const seen = new Set<string>();
		for (const row of rowData.filter(r => r.state !== 'deleted')) {
			if (!row.data.lang || !row.data.term) {
				rp.host.bucket.showAlert({
					type: 'warning',
					title: msg('Validation Error'),
					content: msg('Term and Lang cannot be empty.'),
				});
				return Promise.reject('Term and Lang cannot be empty');
			}
			const key = `${row.data.term}|${row.data.lang}`;
			if (seen.has(key)) duplicates.push(row);
			else seen.add(key);
		}
		if (duplicates.length > 0) {
			rp.host.bucket.showAlert({
				type: 'warning',
				title: msg('Validation Error'),
				content: msg('Each combination of Term and Lang must be unique.'),
			});
			return Promise.reject(duplicates);
		}

		const data = rowData
			.filter(row => row.state !== null)
			.map(row => Object.assign({_state: row.state, _index: row.index}, row.data));
		await rp.ms('definition').post(`dictionary`, data);
		this.editor.removeAttribute('term');
	}

}
